import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import "../screenstyles/TechnicalJoinRequests.css";
import { FaArrowLeft } from "react-icons/fa";

interface TechnicalRequest {
  id: string;

  // بيانات users
  AccountLevel?: string;
  accountType?: string;
  email?: string;
  fullName?: string;
  loginType?: string;
  phoneNumber?: string;
  province?: string;
  uid?: string;
  points?: number;
  profileImageUrl?: string;

  // بيانات requestfortechnicians
  fullAddress?: string;
  emailAddress?: string;
  expertise?: string;
  services?: string;
  years?: string;
  regions?: string;
  partnerships?: string;

  // تواريخ
  createdAt?: any;
  userCreatedAt?: any;
  requestCreatedAt?: any;
  updatedAt?: any;
}

const TechnicalJoinRequests: React.FC = () => {
  const [requests, setRequests] = useState<TechnicalRequest[]>([]);
  const [selectedRequest, setSelectedRequest] =
    useState<TechnicalRequest | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTechnicalRequests = async () => {
      try {
        setLoading(true);

        const usersRef = collection(db, "users");

        const q = query(
          usersRef,
          where("accountType", "==", "طلب تقني بانتظار الموافقة")
        );

        const snapshot = await getDocs(q);

        const data: TechnicalRequest[] = await Promise.all(
          snapshot.docs.map(async (userDoc) => {
            const userData = userDoc.data();

            const requestDocRef = doc(
              db,
              "requestfortechnicians",
              userDoc.id
            );

            const requestDocSnap = await getDoc(requestDocRef);

            const requestData = requestDocSnap.exists()
              ? requestDocSnap.data()
              : {};

            return {
              id: userDoc.id,

              // نخزن تاريخ users باسم مستقل
              userCreatedAt: userData.createdAt,

              // نخزن تاريخ الطلب باسم مستقل
              requestCreatedAt: requestData.createdAt,

              // بيانات users أولاً
              ...userData,

              // بيانات الطلب ثانياً
              // إذا يوجد حقل متشابه مثل fullName أو phoneNumber
              // بيانات الطلب ستغطي على بيانات users لأنها أحدث وأدق للفورم
              ...requestData,

              // نوحد البريد حتى لا يتكرر
              email: requestData.emailAddress || userData.email || "غير متوفر",

              // نوحد تاريخ العرض الرئيسي
              createdAt: requestData.createdAt || userData.createdAt,
            };
          })
        );

        setRequests(data);

        if (data.length > 0) {
          setSelectedRequest(data[0]);
        }
      } catch (error) {
        console.error("Error fetching technical requests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTechnicalRequests();
  }, []);

  const handleApproveRequest = async () => {
  if (!selectedRequest) return;

  const confirmApprove = window.confirm(
    `هل أنت متأكد من قبول طلب ${selectedRequest.fullName || "هذا المستخدم"}؟`
  );

  if (!confirmApprove) return;

  try {
    const userRef = doc(db, "users", selectedRequest.id);

    await updateDoc(userRef, {
      accountType: "منصب",
      requestStatus: "approved",
      approvedAt: serverTimestamp(),
    });

    setRequests((prev) =>
      prev.filter((request) => request.id !== selectedRequest.id)
    );

    setSelectedRequest(null);

    alert("تم قبول الطلب وتحويل الحساب إلى منصب بنجاح");
  } catch (error) {
    console.error("Error approving request:", error);
    alert("حدث خطأ أثناء قبول الطلب");
  }
};
  const formatDate = (value: any) => {
    if (!value) return "غير متوفر";

    try {
      if (value?.toDate) {
        return value.toDate().toLocaleString("ar-IQ");
      }

      return new Date(value).toLocaleString("ar-IQ");
    } catch {
      return "غير متوفر";
    }
  };

  if (loading) {
    return <div className="technical-page">جاري تحميل الطلبات...</div>;
  }

  return (
    <div className="technical-page" dir="rtl">
      <div className="technical-header">
        <div className="technical-header-content">
          <h2>طلبات انضمام التقنيين</h2>
          <p>عرض جميع طلبات التقنيين بانتظار الموافقة</p>
        </div>

        <button
          className="back-icon-btn"
          onClick={() => navigate("/")}
          title="رجوع"
        >
          <FaArrowLeft />
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="empty-box">لا توجد طلبات تقنية حالياً</div>
      ) : (
        <div className="requests-layout">
          <div className="requests-list">
            {requests.map((request) => (
              <div
                key={request.id}
                className={`request-card ${
                  selectedRequest?.id === request.id ? "active" : ""
                }`}
                onClick={() => setSelectedRequest(request)}
              >
                <img
                  src={request.profileImageUrl || "/default-user.png"}
                  alt={request.fullName || "User"}
                  className="request-image"
                />

                <div className="request-info">
                  <h3>{request.fullName || "بدون اسم"}</h3>
                  <p>{request.phoneNumber || "لا يوجد رقم هاتف"}</p>
                  <span>{request.province || "بدون محافظة"}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="request-details">
            {!selectedRequest ? (
              <div className="select-message">
                اختر طلب من القائمة لعرض التفاصيل بالكامل
              </div>
            ) : (
              <>
                <div className="details-top">
                  <img
                    src={selectedRequest.profileImageUrl || "/default-user.png"}
                    alt={selectedRequest.fullName || "User"}
                    className="details-image"
                  />

                  <div>
                    <h2>{selectedRequest.fullName || "بدون اسم"}</h2>
                    <p>{selectedRequest.accountType || "غير متوفر"}</p>
                  </div>
                </div>

                <div className="details-grid">
                  <div>
                    <label>الاسم الكامل</label>
                    <p>{selectedRequest.fullName || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>رقم الهاتف</label>
                    <p>{selectedRequest.phoneNumber || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>البريد الإلكتروني</label>
                    <p>{selectedRequest.email || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>المحافظة</label>
                    <p>{selectedRequest.province || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>العنوان الكامل</label>
                    <p>{selectedRequest.fullAddress || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>نوع الحساب</label>
                    <p>{selectedRequest.accountType || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>مستوى الحساب</label>
                    <p>{selectedRequest.AccountLevel || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>نوع تسجيل الدخول</label>
                    <p>{selectedRequest.loginType || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>الاختصاص الأساسي</label>
                    <p>{selectedRequest.expertise || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>سنوات الخبرة</label>
                    <p>{selectedRequest.services || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>الشهادات التقنية</label>
                    <p>{selectedRequest.years || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>خبرة العمل الميداني</label>
                    <p>{selectedRequest.regions || "غير متوفر"}</p>
                  </div>

                  <div className="full-width">
                    <label>المناطق التي يغطيها</label>
                    <p>{selectedRequest.partnerships || "غير متوفر"}</p>
                  </div>

                  <div>
                    <label>تاريخ تقديم الطلب</label>
                    <p>{formatDate(selectedRequest.requestCreatedAt)}</p>
                  </div>

                  <div>
                    <label>تاريخ إنشاء الحساب</label>
                    <p>{formatDate(selectedRequest.userCreatedAt)}</p>
                  </div>

                  <div className="full-width">
                    <label>UID</label>
                    <p>{selectedRequest.uid || selectedRequest.id}</p>
                  </div>
                </div>

                <div className="details-actions">
                 <button className="approve-btn" onClick={handleApproveRequest}>
  قبول الطلب
</button>
                  <button className="reject-btn">رفض الطلب</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicalJoinRequests;